// Силуэты платформ для слоя карты: рисуются в canvas и регистрируются в
// MapLibre как образы `buoy-ic-<тип>-<свежесть>`.
//
// Тип различает ФОРМА, свежесть — ЦВЕТ (см. buoyLayer.ts). Силуэты нарочно
// грубые: на рабочем зуме точка занимает два десятка пикселей, и подробность в
// ней всё равно не читается — читается отличие одной формы от другой.

import type maplibregl from 'maplibre-gl'
import { BUOY_ICON_PREFIX, OTHER_TYPE_ICON, freshnessColor, iconMatrix } from './buoyLayer'
import type { BuoyFreshness } from './buoyLayer'

/** Логический размер образа (px); на экране столько же при любом DPR. */
export const BUOY_ICON_PX = 20

function circle(ctx: CanvasRenderingContext2D, x: number, y: number, r: number) {
  ctx.beginPath()
  ctx.arc(x, y, r, 0, Math.PI * 2)
  ctx.fill()
}

/** Рисует силуэт одного типа. Возвращает ImageData или null, если canvas
 *  недоступен (jsdom в тестах, старый браузер) — вызывающий тогда просто не
 *  регистрирует образ. */
export function drawBuoyIcon(type: string, color: string, ratio: number): ImageData | null {
  const s = BUOY_ICON_PX * ratio
  const canvas = document.createElement('canvas')
  canvas.width = s
  canvas.height = s
  const ctx = canvas.getContext('2d')
  if (!ctx) return null

  const mid = s / 2
  const unit = s / 20

  // Светлый диск-подложка: силуэт обязан читаться на светлой carto-подложке.
  ctx.fillStyle = 'rgba(255,255,255,0.85)'
  circle(ctx, mid, mid, mid - unit * 0.5)
  ctx.strokeStyle = color
  ctx.lineWidth = unit * 1.2
  ctx.beginPath()
  ctx.arc(mid, mid, mid - unit * 1.1, 0, Math.PI * 2)
  ctx.stroke()

  ctx.fillStyle = color
  ctx.strokeStyle = color
  ctx.lineCap = 'round'
  ctx.lineWidth = unit * 1.6

  switch (type) {
    case 'MO': // заякоренный буй: корпус и мачта, снизу линия якоря
      circle(ctx, mid, mid + unit * 1.5, unit * 3)
      ctx.beginPath()
      ctx.moveTo(mid, mid - unit * 5)
      ctx.lineTo(mid, mid - unit * 0.5)
      ctx.stroke()
      break
    case 'DB': // дрейфующий буй: корпус и парус-подвес под ним
      circle(ctx, mid, mid - unit * 1.5, unit * 2.6)
      ctx.beginPath()
      ctx.moveTo(mid, mid + unit * 0.5)
      ctx.lineTo(mid, mid + unit * 4.5)
      ctx.moveTo(mid - unit * 2.4, mid + unit * 4.5)
      ctx.lineTo(mid + unit * 2.4, mid + unit * 4.5)
      ctx.stroke()
      break
    case 'PF': // профилирующий буй Argo: вытянутая капсула со стрелкой вглубь
      ctx.beginPath()
      ctx.roundRect(mid - unit * 2, mid - unit * 5, unit * 4, unit * 7, unit * 2)
      ctx.fill()
      ctx.beginPath()
      ctx.moveTo(mid, mid + unit * 2.5)
      ctx.lineTo(mid, mid + unit * 5)
      ctx.stroke()
      break
    case 'TS': // судно: корпус клином
    case 'FB':
      ctx.beginPath()
      ctx.moveTo(mid - unit * 5, mid - unit)
      ctx.lineTo(mid + unit * 5, mid - unit)
      ctx.lineTo(mid + unit * 2, mid + unit * 3.5)
      ctx.lineTo(mid - unit * 3, mid + unit * 3.5)
      ctx.closePath()
      ctx.fill()
      if (type === 'FB') {
        ctx.beginPath()
        ctx.moveTo(mid - unit * 5, mid + unit * 5)
        ctx.lineTo(mid + unit * 5, mid + unit * 5)
        ctx.stroke()
      }
      break
    case 'GL': // глайдер: крыло
    case 'SD':
      ctx.beginPath()
      ctx.moveTo(mid - unit * 5, mid + unit * 2)
      ctx.lineTo(mid + unit * 5, mid + unit * 2)
      ctx.lineTo(mid, mid - unit * 5)
      ctx.closePath()
      ctx.fill()
      break
    case 'CT': // зонд с судна: точка с уходящим вниз тросом
    case 'XB':
      circle(ctx, mid, mid - unit * 3, unit * 2)
      ctx.beginPath()
      ctx.moveTo(mid, mid - unit)
      ctx.lineTo(mid, mid + unit * 5)
      ctx.stroke()
      break
    case 'AD': // профилограф течений: три восходящих луча
      ctx.beginPath()
      for (const dx of [-3, 0, 3]) {
        ctx.moveTo(mid + unit * dx * 0.8, mid + unit * 4)
        ctx.lineTo(mid + unit * dx, mid - unit * 4)
      }
      ctx.stroke()
      break
    default: // неизвестный тип: просто точка, но точка ЕСТЬ
      circle(ctx, mid, mid, unit * 3)
  }

  return ctx.getImageData(0, 0, s, s)
}

/** Зарегистрировать все образы в карте. Идемпотентно: повторный вызов после
 *  пересоздания стиля не падает. */
export function ensureBuoyIcons(map: maplibregl.Map): void {
  const ratio = Math.max(1, Math.round(window.devicePixelRatio || 1))
  for (const { id, type, state } of iconMatrix()) {
    if (map.hasImage(id)) continue
    const image = drawBuoyIcon(
      type === OTHER_TYPE_ICON ? OTHER_TYPE_ICON : type,
      freshnessColor(state as BuoyFreshness),
      ratio,
    )
    if (image) map.addImage(id, image, { pixelRatio: ratio })
  }
}

export { BUOY_ICON_PREFIX }
